import {
  type CreateDemoPublicBookingRequest,
  type DemoAppointmentPatientSex,
  type DemoPublicBookingReceipt,
  type DemoPublicSchedulingConfiguration,
  OntarioHealthNumberSchema,
  PUBLIC_TERMS_CHANGED_ERROR_CODE,
  type PublicAvailabilitySlot,
  type PublicBookingConsent,
  type PublicBookingResponse,
} from "@emr/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AddToCalendar } from "../AddToCalendar";
import { ConsentAgreement } from "../consent/ConsentAgreement";
import {
  bookingConsent,
  type ConsentChoices,
  initialConsentChoices,
  messagingOfferedDisclosure,
  termsAgreed,
  termsChanged,
  termsRequiredToBook,
} from "../consent/consentChoices";
import { Icon } from "../Icon";
import {
  formatBirthDate,
  formatDateTimeRange,
  localDateOf,
  slotDurationMinutes,
  telephoneHref,
} from "../lib/appointmentTime";
import { sexOptions } from "../lib/patientSex";
import { isSchemaError } from "../lib/validation";
import { RequiredMarker } from "../RequiredMarker";
import { StepProgress } from "../StepProgress";
import { addDays, monthOf, namesEveryTime } from "./availabilityCalendar";
import { BookingChallenge } from "./BookingChallenge";
import { ClinicHoursToday, ClinicHoursWeek } from "./ClinicHours";
import { DateTimePicker } from "./DateTimePicker";
import { DemoBookingApiError, demoSchedulingClient } from "./demoSchedulingClient";
import { embedParent, readEmbedContext } from "./embedParent";
import { BOOKING_RESIZE_MESSAGE } from "./embedProtocol";
import { scrollBehavior } from "./motion";
import {
  createPublicSchedulingClient,
  demoSchedulingEnabled,
  type PatientBookingConfiguration,
  type PatientSchedulingClient,
} from "./publicSchedulingClient";
import { canonicalizeBookingLocation } from "./routing";
import { useAvailabilityCalendar } from "./useAvailabilityCalendar";
import { type VerifiedPatient, VerifyPatientStep } from "./VerifyPatientStep";

type BookingStep = 0 | 1 | 2 | 3;
type PatientKind = "new" | "returning";
type PatientSex = "" | DemoAppointmentPatientSex;

interface BookingDraft {
  readonly kind: PatientKind;
  readonly appointmentTypeId: string;
  readonly providerId: string;
  /** The chosen day on the clinic's calendar; "" until the calendar picks or the patient does. */
  readonly date: string;
  /** The calendar month on screen; "" to show the chosen day's month (or this month). */
  readonly month: string;
  readonly slot: PublicAvailabilitySlot | null;
  readonly displayName: string;
  readonly dateOfBirth: string;
  readonly sex: PatientSex;
  readonly phone: string;
  readonly email: string;
  readonly reason: string;
  /**
   * The Ontario health card number a returning patient may give, as typed, to help the clinic
   * find their chart (PA-10): only where healthCardOffered says so. Held in this draft only,
   * never in a URL, browser storage or a query key, and cleared with the draft.
   */
  readonly healthNumber: string;
  /** The agreement lines on the review step (the clinic lane only). */
  readonly consent: ConsentChoices;
}

const stepLabels = ["Service", "Date & time", "Your details", "Confirm"] as const;
const stepHeadings = [
  "Choose your visit",
  "Choose a date and time",
  "Your details",
  "Review and book",
] as const;
const backLabels = ["", "Back to visit", "Back to date and time", "Back to details"] as const;
/** Visit, clinician or patient-kind changes start the calendar over. */
const freshCalendar = { date: "", month: "", slot: null } as const;

function localDateValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dateAfter(days: number): string {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return localDateValue(date);
}

function idempotencyKey(): string {
  const suffix =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `patient-booking-${suffix}`;
}

function initialDraft(): BookingDraft {
  return {
    kind: "returning",
    appointmentTypeId: "",
    providerId: "",
    date: "",
    month: "",
    slot: null,
    displayName: "",
    dateOfBirth: "",
    sex: "",
    phone: "",
    email: "",
    reason: "",
    healthNumber: "",
    consent: initialConsentChoices,
  };
}

/**
 * Whether the details step offers the optional health card number (PA-10): to a returning
 * patient at a clinic that books anyone, on the clinic lane. Never to a new patient, never at a
 * clinic that books existing patients only (its check already matched a card), and never in the
 * synthetic demo. It is never required.
 */
function healthCardOffered(
  demo: boolean,
  configuration: PatientBookingConfiguration | undefined,
  draft: BookingDraft,
): boolean {
  return (
    !demo &&
    configuration !== undefined &&
    configuration.onlineBookingAudience !== "existing_patients" &&
    draft.kind === "returning"
  );
}

/** The health card number to send: only when offered and filled in. */
function bookingHealthNumber(offered: boolean, draft: BookingDraft): string | undefined {
  return offered && draft.healthNumber.trim() !== "" ? draft.healthNumber : undefined;
}

/** Why an optional health card number cannot be sent; "" when it can, or when it is blank. */
function healthCardProblem(value: string): string {
  if (value.trim() === "") return "";
  const result = OntarioHealthNumberSchema.safeParse(value);
  return result.success ? "" : (result.error.issues[0]?.message ?? "Check the health card number");
}

function visitModeLabel(mode: "in_person" | "phone" | "video"): string {
  if (mode === "in_person") return "In person";
  if (mode === "phone") return "Phone";
  return "Secure video";
}

function emailLooksValid(value: string): boolean {
  return value.length === 0 || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/** Who the patient will see: a named physician, or the clinic's choice when it assigns one. */
function clinicianName(
  configuration: DemoPublicSchedulingConfiguration,
  providerId: string | undefined,
): string {
  if (providerId === undefined) return "Assigned by the clinic";
  return (
    configuration.providers.find((provider) => provider.id === providerId)?.displayName ??
    "Clinic clinician"
  );
}

function validateStep(
  step: BookingStep,
  draft: BookingDraft,
  configuration: PatientBookingConfiguration,
  healthCard: boolean,
): string[] {
  if (step === 0) {
    return [
      draft.kind === "new" && !configuration.allowNewPatientsOnline
        ? "Online booking is currently available to returning patients only"
        : "",
      !draft.appointmentTypeId ? "Choose a visit type" : "",
    ].filter(Boolean);
  }
  if (step === 1) return draft.slot === null ? ["Choose a time"] : [];
  if (step === 2) {
    return [
      !draft.displayName.trim() ? "Full name is required" : "",
      !draft.dateOfBirth ? "Date of birth is required" : "",
      // Optional: checked only when filled in.
      healthCard ? healthCardProblem(draft.healthNumber) : "",
      !draft.sex ? "Sex is required" : "",
      !draft.phone.trim() ? "Mobile number is required" : "",
      !emailLooksValid(draft.email.trim()) ? "Enter a valid email address" : "",
      !draft.reason.trim() ? "Add the main reason for your visit" : "",
    ].filter(Boolean);
  }
  const documents = configuration.consentDocuments;
  return documents !== undefined && !termsAgreed(documents, draft.consent)
    ? [termsRequiredToBook]
    : [];
}

export function isOnlineBookingOpen(
  configuration: Pick<DemoPublicSchedulingConfiguration, "mode" | "onlineBookingEnabled">,
): boolean {
  return configuration.onlineBookingEnabled && configuration.mode !== "walk_in_only";
}

function BookingBrand({ clinicName }: { readonly clinicName: string }) {
  return (
    <header className="booking-topbar">
      <div className="patient-brand">
        <div>
          <img className="patient-bob-wordmark" src="/brand/bob/wordmark.svg" alt="Bob Doc" />
          <small>for {clinicName}</small>
        </div>
      </div>
      <div className="booking-topbar-actions">
        <span className="secure-label">
          <Icon name="shield" />
          {demoSchedulingEnabled() ? "Synthetic booking demo" : "Clinic appointment booking"}
        </span>
      </div>
    </header>
  );
}

/** A full-page notice in place of the booking form, ending with the clinic's hours and place. */
function ClinicNotice({
  configuration,
  embedded,
  icon,
  heading,
  children,
}: {
  readonly configuration: PatientBookingConfiguration;
  readonly embedded: boolean;
  readonly icon: "clock" | "phone";
  readonly heading: string;
  readonly children: ReactNode;
}) {
  return (
    <div className={`booking-app ${embedded ? "booking-embedded" : ""}`}>
      <BookingBrand clinicName={configuration.clinicName} />
      <main className="booking-state-card" aria-labelledby="booking-unavailable-heading">
        <span className="booking-state-icon">
          <Icon name={icon} />
        </span>
        <p className="booking-eyebrow">Online scheduling</p>
        <h1 id="booking-unavailable-heading" tabIndex={-1}>
          {heading}
        </h1>
        {children}
        <ClinicHoursWeek hours={configuration.clinicHours} timeZone={configuration.timeZone} />
        <div className="booking-location">
          <Icon name="location" />
          <span>
            <strong>{configuration.clinicName}</strong>
            {configuration.clinicAddress}
          </span>
        </div>
      </main>
    </div>
  );
}

/** "Call the clinic", with the site phone as a link when the clinic has one on file. */
function CallTheClinic({
  configuration,
  message,
}: {
  readonly configuration: DemoPublicSchedulingConfiguration;
  readonly message: string;
}) {
  const phone = configuration.clinicPhone;
  if (phone === undefined) return <p>Please contact the clinic to book an appointment.</p>;
  const href = telephoneHref(phone);
  return (
    <>
      <p>{message}</p>
      {href === null ? (
        <p>Clinic phone: {phone}</p>
      ) : (
        <a className="continue-button booking-link-button" href={href}>
          <Icon name="phone" />
          Call {phone}
        </a>
      )}
    </>
  );
}

export function BookingUnavailable({
  configuration,
  embedded,
}: {
  readonly configuration: PatientBookingConfiguration;
  readonly embedded: boolean;
}) {
  if (configuration.mode === "walk_in_only") {
    return (
      <ClinicNotice
        configuration={configuration}
        embedded={embedded}
        icon="clock"
        heading="This clinic is operating as walk-in only."
      >
        <p>
          Online booking is not available. Please see the front desk when you arrive during clinic
          hours.
        </p>
      </ClinicNotice>
    );
  }
  return (
    <ClinicNotice
      configuration={configuration}
      embedded={embedded}
      icon="phone"
      heading="Online booking isn't available."
    >
      <CallTheClinic
        configuration={configuration}
        message="Please call the clinic to book an appointment."
      />
    </ClinicNotice>
  );
}

/** The attempts to confirm these details online are used up. */
function VerificationLocked({
  configuration,
  embedded,
}: {
  readonly configuration: PatientBookingConfiguration;
  readonly embedded: boolean;
}) {
  return (
    <ClinicNotice
      configuration={configuration}
      embedded={embedded}
      icon="phone"
      heading="We couldn't verify your details online."
    >
      <CallTheClinic configuration={configuration} message="Please call the clinic to book." />
    </ClinicNotice>
  );
}

/**
 * A website the clinic did not list framed its booking page (PA-12): no form and nothing about
 * the clinic, only a link that opens the clinic's booking page in a new tab.
 */
function EmbedNotAllowed({
  bookingPath,
  embedded,
}: {
  readonly bookingPath: string;
  readonly embedded: boolean;
}) {
  return (
    <div className={`booking-app ${embedded ? "booking-embedded" : ""}`}>
      <BookingBrand clinicName="the clinic" />
      <main className="booking-state-card" aria-labelledby="booking-embed-heading">
        <span className="booking-state-icon">
          <Icon name="clock" />
        </span>
        <p className="booking-eyebrow">Online booking</p>
        <h1 id="booking-embed-heading">Book on the clinic's booking page</h1>
        <p>Booking isn't available on this website. Open the clinic's booking page to book.</p>
        <a
          className="continue-button booking-link-button"
          href={bookingPath}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open the booking page
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      </main>
    </div>
  );
}

function ConfigurationError({
  embedded,
  invalidLink,
  retry,
}: {
  readonly embedded: boolean;
  readonly invalidLink: boolean;
  readonly retry: () => void;
}) {
  return (
    <div className={`booking-app ${embedded ? "booking-embedded" : ""}`}>
      <BookingBrand clinicName="the clinic" />
      <main className="booking-state-card" aria-labelledby="booking-error-heading">
        <span className="booking-state-icon booking-state-icon-error">
          <Icon name="clock" />
        </span>
        {invalidLink ? (
          <>
            <p className="booking-eyebrow">Booking link</p>
            <h1 id="booking-error-heading">This booking link is not valid.</h1>
            <p>
              Use the booking link on your clinic's website, or see the front desk. Your information
              was not submitted.
            </p>
          </>
        ) : (
          <>
            <p className="booking-eyebrow">Connection problem</p>
            <h1 id="booking-error-heading">Booking is temporarily unavailable.</h1>
            <p>
              The scheduling service could not be reached. Open the booking link provided by your
              clinic or try again. Your information was not submitted.
            </p>
            <button type="button" className="continue-button" onClick={retry}>
              Try again
            </button>
          </>
        )}
      </main>
    </div>
  );
}

/** Physicians the patient may pick: none when the clinic assigns them. */
function bookableProviders(configuration: DemoPublicSchedulingConfiguration) {
  return configuration.providers.filter(
    (provider) => provider.active && provider.acceptingOnlineBookings,
  );
}

function VisitStep({
  configuration,
  draft,
  update,
  verifiedPatient,
}: {
  readonly configuration: DemoPublicSchedulingConfiguration;
  readonly draft: BookingDraft;
  readonly update: (next: Partial<BookingDraft>) => void;
  /** An existing patient who confirmed their details: they book as a returning patient. */
  readonly verifiedPatient: VerifiedPatient | null;
}) {
  const appointmentTypes = configuration.appointmentTypes.filter(
    (type) =>
      type.active &&
      type.patientBookable &&
      (draft.kind === "returning" || type.newPatientsAllowed),
  );
  const providers = bookableProviders(configuration);
  const selectedType = appointmentTypes.find((type) => type.id === draft.appointmentTypeId);
  // Before a visit type is chosen, the choice shows only if every listed type offers it.
  const choosesClinician =
    providers.length > 0 &&
    (selectedType === undefined
      ? appointmentTypes.every((type) => type.patientChoosesPhysician)
      : selectedType.patientChoosesPhysician);

  return (
    <>
      {verifiedPatient === null ? (
        <PatientKindChoice configuration={configuration} draft={draft} update={update} />
      ) : (
        <p className="booking-welcome">
          <Icon name="person" />
          {verifiedPatient.firstName === undefined
            ? "Welcome back."
            : `Welcome back, ${verifiedPatient.firstName}.`}
        </p>
      )}

      <fieldset className="booking-fieldset">
        <legend>
          Visit type
          <RequiredMarker />
        </legend>
        <div className="booking-type-grid">
          {appointmentTypes.map((type) => (
            <label key={type.id} className={draft.appointmentTypeId === type.id ? "selected" : ""}>
              <input
                type="radio"
                name="appointment-type"
                required
                checked={draft.appointmentTypeId === type.id}
                onChange={() =>
                  update({
                    appointmentTypeId: type.id,
                    ...freshCalendar,
                    ...(type.patientChoosesPhysician ? {} : { providerId: "" }),
                  })
                }
              />
              <span>
                <strong>{type.name}</strong>
                {type.description.trim() === "" ? null : <small>{type.description}</small>}
                <em>
                  {type.durationMinutes} min · {visitModeLabel(type.visitMode)}
                </em>
              </span>
              <i>
                <Icon name="check" />
              </i>
            </label>
          ))}
        </div>
      </fieldset>

      {choosesClinician ? (
        <ClinicianChoice providers={providers} draft={draft} update={update} />
      ) : selectedType !== undefined ? (
        <p className="booking-time-note booking-assigned-note">
          <Icon name="person" />
          The clinic assigns the clinician for this visit.
        </p>
      ) : null}
    </>
  );
}

function PatientKindChoice({
  configuration,
  draft,
  update,
}: {
  readonly configuration: DemoPublicSchedulingConfiguration;
  readonly draft: BookingDraft;
  readonly update: (next: Partial<BookingDraft>) => void;
}) {
  return (
    <fieldset className="booking-fieldset">
      <legend>
        Have you visited this clinic before?
        <RequiredMarker />
      </legend>
      <div className="booking-choice-grid booking-choice-grid-two">
        <label className={draft.kind === "returning" ? "selected" : ""}>
          <input
            type="radio"
            name="patient-kind"
            required
            checked={draft.kind === "returning"}
            onChange={() => update({ kind: "returning", appointmentTypeId: "", ...freshCalendar })}
          />
          <span className="booking-choice-mark">
            <Icon name="person" />
          </span>
          <span>
            <strong>Returning patient</strong>
          </span>
          <i>
            <Icon name="check" />
          </i>
        </label>
        <label
          className={draft.kind === "new" ? "selected" : ""}
          aria-disabled={!configuration.allowNewPatientsOnline}
        >
          <input
            type="radio"
            name="patient-kind"
            required
            checked={draft.kind === "new"}
            disabled={!configuration.allowNewPatientsOnline}
            onChange={() =>
              update({ kind: "new", appointmentTypeId: "", ...freshCalendar, healthNumber: "" })
            }
          />
          <span className="booking-choice-mark">
            <Icon name="sparkles" />
          </span>
          <span>
            <strong>New patient</strong>
            {configuration.allowNewPatientsOnline ? null : (
              <small>Call the clinic to register</small>
            )}
          </span>
          <i>
            <Icon name="check" />
          </i>
        </label>
      </div>
    </fieldset>
  );
}

function ClinicianChoice({
  providers,
  draft,
  update,
}: {
  readonly providers: ReturnType<typeof bookableProviders>;
  readonly draft: BookingDraft;
  readonly update: (next: Partial<BookingDraft>) => void;
}) {
  return (
    <fieldset className="booking-fieldset">
      <legend>Clinician</legend>
      <div className="booking-provider-grid">
        <label className={draft.providerId === "" ? "selected" : ""}>
          <input
            type="radio"
            name="provider"
            checked={draft.providerId === ""}
            onChange={() => update({ providerId: "", ...freshCalendar })}
          />
          <span className="booking-avatar">Any</span>
          <span>
            <strong>Any available clinician</strong>
            <small>Most times</small>
          </span>
          <i>
            <Icon name="check" />
          </i>
        </label>
        {providers.map((provider) => (
          <label key={provider.id} className={draft.providerId === provider.id ? "selected" : ""}>
            <input
              type="radio"
              name="provider"
              checked={draft.providerId === provider.id}
              onChange={() => update({ providerId: provider.id, ...freshCalendar })}
            />
            <span className="booking-avatar">
              {provider.displayName
                .split(/\s+/)
                .slice(-2)
                .map((part) => part[0])
                .join("")}
            </span>
            <span>
              <strong>{provider.displayName}</strong>
              <small>{provider.credentials}</small>
            </span>
            <i>
              <Icon name="check" />
            </i>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function TimeStep({
  configuration,
  draft,
  update,
  availability,
  today,
  lastDay,
  month,
}: {
  readonly configuration: DemoPublicSchedulingConfiguration;
  readonly draft: BookingDraft;
  readonly update: (next: Partial<BookingDraft>) => void;
  readonly availability: ReturnType<typeof useAvailabilityCalendar>;
  /** Today and the booking window's last day, on the clinic's calendar. */
  readonly today: string;
  readonly lastDay: string;
  readonly month: string;
}) {
  const appointmentType = configuration.appointmentTypes.find(
    (type) => type.id === draft.appointmentTypeId,
  );
  const onDate = useCallback((date: string) => update({ date, slot: null }), [update]);
  const onMonthChange = useCallback((next: string) => update({ month: next }), [update]);
  const choosesClinician =
    appointmentType?.patientChoosesPhysician === true &&
    bookableProviders(configuration).length > 0;
  const phone = configuration.clinicPhone;
  const phoneHref = phone === undefined ? null : telephoneHref(phone);

  return (
    <DateTimePicker
      headingLevel={2}
      availability={availability}
      month={month}
      onMonthChange={onMonthChange}
      today={today}
      firstDay={today}
      lastDay={lastDay}
      date={draft.date}
      onDate={onDate}
      slot={draft.slot}
      onSlot={(slot) => update({ slot })}
      timeZone={configuration.timeZone}
      physicianName={(providerId) => clinicianName(configuration, providerId)}
      // The visit and clinician choices are on the first step, not here.
      emptyHint="Pick another day."
      noTimes={
        <>
          <p>
            {choosesClinician
              ? "Go back to try another visit type or clinician, or call the clinic."
              : "Go back to try another visit type, or call the clinic."}
          </p>
          {phone === undefined ? null : phoneHref === null ? (
            <p>Clinic phone: {phone}</p>
          ) : (
            <a className="outline-button booking-link-button" href={phoneHref}>
              <Icon name="phone" />
              Call {phone}
            </a>
          )}
        </>
      }
      name="appointment-slot"
    />
  );
}

function DetailsStep({
  draft,
  update,
  verified,
  healthCard,
}: {
  readonly draft: BookingDraft;
  readonly update: (next: Partial<BookingDraft>) => void;
  /** The date of birth was confirmed with the health card and cannot change here. */
  readonly verified: boolean;
  /** Offer the optional health card number (healthCardOffered). */
  readonly healthCard: boolean;
}) {
  return (
    <>
      {demoSchedulingEnabled() ? (
        <p className="booking-demo-note">
          <Icon name="warning" />
          Demo only: use made-up details.
        </p>
      ) : null}
      <div className="booking-form-grid booking-form-grid-two">
        <label>
          <span>
            Full name
            <RequiredMarker />
          </span>
          <input
            autoComplete="off"
            required
            value={draft.displayName}
            onChange={(event) => update({ displayName: event.target.value })}
          />
        </label>
        {verified ? (
          <label>
            <span>Date of birth</span>
            <input type="date" autoComplete="off" readOnly value={draft.dateOfBirth} />
            <small>Confirmed with your health card.</small>
          </label>
        ) : (
          <label>
            <span>
              Date of birth
              <RequiredMarker />
            </span>
            <input
              type="date"
              autoComplete="off"
              required
              max={dateAfter(0)}
              value={draft.dateOfBirth}
              onChange={(event) => update({ dateOfBirth: event.target.value })}
            />
          </label>
        )}
      </div>
      <fieldset className="booking-fieldset booking-sex-fieldset">
        <legend>
          Sex
          <RequiredMarker />
        </legend>
        <div className="booking-sex-options">
          {sexOptions.map((option) => (
            <label key={option.value} className={draft.sex === option.value ? "selected" : ""}>
              <input
                type="radio"
                name="patient-sex"
                required
                checked={draft.sex === option.value}
                onChange={() => update({ sex: option.value })}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <div className="booking-form-grid booking-form-grid-two">
        <label>
          <span>
            Mobile number
            <RequiredMarker />
          </span>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="off"
            required
            value={draft.phone}
            onChange={(event) => update({ phone: event.target.value })}
          />
          {/* The demo's own note says no message is sent. */}
          {demoSchedulingEnabled() ? null : <small>Used to contact you about this booking.</small>}
        </label>
        <label>
          <span>Email</span>
          <input
            type="email"
            inputMode="email"
            autoComplete="off"
            value={draft.email}
            onChange={(event) => update({ email: event.target.value })}
          />
        </label>
      </div>
      {healthCard ? (
        <div className="booking-form-grid booking-form-grid-two">
          <label>
            <span>
              Ontario health card <span className="booking-field-optional">(optional)</span>
            </span>
            <input
              autoComplete="off"
              inputMode="numeric"
              maxLength={14}
              value={draft.healthNumber}
              onChange={(event) => update({ healthNumber: event.target.value })}
            />
            <small>Helps the clinic find your chart.</small>
          </label>
        </div>
      ) : null}
      <div className="booking-form-grid">
        <label>
          <span>
            Main reason for visit
            <RequiredMarker />
          </span>
          <textarea
            rows={3}
            required
            value={draft.reason}
            onChange={(event) => update({ reason: event.target.value })}
            placeholder="For example: cough for three days"
          />
          <small>A few words is enough.</small>
        </label>
      </div>
    </>
  );
}

/** A review section's title with its Edit button, which goes back to that step. */
function ReviewSectionHeader({
  title,
  editLabel,
  onEdit,
  busy,
}: {
  readonly title: string;
  /** Finishes "Edit …" for screen readers, such as "date and time". */
  readonly editLabel: string;
  readonly onEdit: () => void;
  /** The booking is being sent: what it sends can no longer change. */
  readonly busy: boolean;
}) {
  return (
    <header>
      <h2>{title}</h2>
      <button type="button" className="booking-edit-button" onClick={onEdit} disabled={busy}>
        Edit <span className="sr-only">{editLabel}</span>
      </button>
    </header>
  );
}

function ReviewStep({
  configuration,
  draft,
  update,
  embedded,
  healthCard,
  edit,
  busy,
}: {
  readonly configuration: PatientBookingConfiguration;
  readonly draft: BookingDraft;
  readonly update: (next: Partial<BookingDraft>) => void;
  readonly embedded: boolean;
  /** The details step offered the health card number: say whether it was given, never echo it. */
  readonly healthCard: boolean;
  /** Goes back to a step to change it. */
  readonly edit: (step: BookingStep) => void;
  /** The booking is being sent: the Edit buttons wait, like the back link and step indicator. */
  readonly busy: boolean;
}) {
  const appointmentType = configuration.appointmentTypes.find(
    (type) => type.id === draft.appointmentTypeId,
  );
  return (
    <>
      <div className="booking-review">
        <section>
          <ReviewSectionHeader
            title="Appointment"
            editLabel="date and time"
            onEdit={() => edit(1)}
            busy={busy}
          />
          <dl>
            <div className="wide">
              <dt>When</dt>
              <dd>
                {draft.slot
                  ? formatDateTimeRange(
                      draft.slot.startAt,
                      draft.slot.endAt,
                      configuration.timeZone,
                    )
                  : "Not selected"}
              </dd>
            </div>
            <div>
              <dt>Visit</dt>
              <dd>
                {appointmentType?.name ?? "Not selected"}
                {draft.slot
                  ? ` · ${slotDurationMinutes(draft.slot.startAt, draft.slot.endAt)} min`
                  : ""}
              </dd>
            </div>
            <div>
              <dt>Clinician</dt>
              <dd>
                {draft.slot ? clinicianName(configuration, draft.slot.providerId) : "Not selected"}
              </dd>
            </div>
            <div className="wide">
              <dt>Location</dt>
              <dd>{configuration.clinicAddress}</dd>
            </div>
          </dl>
        </section>
        <section>
          <ReviewSectionHeader
            title="Your details"
            editLabel="your details"
            onEdit={() => edit(2)}
            busy={busy}
          />
          <dl>
            <div>
              <dt>Name</dt>
              <dd>{draft.displayName}</dd>
            </div>
            <div>
              <dt>Patient</dt>
              <dd>{draft.kind === "new" ? "New patient" : "Returning patient"}</dd>
            </div>
            <div>
              <dt>Date of birth</dt>
              <dd>{formatBirthDate(draft.dateOfBirth)}</dd>
            </div>
            {healthCard ? (
              <div>
                <dt>Health card</dt>
                <dd>{draft.healthNumber.trim() === "" ? "Not provided" : "Provided"}</dd>
              </div>
            ) : null}
            <div>
              <dt>Mobile</dt>
              <dd>{draft.phone}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{draft.email || "Not provided"}</dd>
            </div>
            <div className="wide">
              <dt>Reason</dt>
              <dd>{draft.reason}</dd>
            </div>
          </dl>
        </section>
      </div>
      {configuration.consentDocuments === undefined ? null : (
        <ConsentAgreement
          documents={configuration.consentDocuments}
          choices={draft.consent}
          onChange={(consent) => update({ consent })}
          embedded={embedded}
        />
      )}
      <p className="booking-reminder-disclosure">
        <Icon name="phone" />
        <span>
          {demoSchedulingEnabled()
            ? "Demo only: confirmations and reminders are simulated. No real message is sent."
            : configuration.consentDocuments?.electronicMessaging
              ? // The clinic offers messages (PA-8): only with the patient's agreement above.
                messagingOfferedDisclosure
              : "This clinic doesn't send texts or emails. Keep your confirmation page and private link."}
        </span>
      </p>
    </>
  );
}

function BookingConfirmation({
  configuration,
  receipt,
  embedded,
  restart,
}: {
  readonly configuration: DemoPublicSchedulingConfiguration;
  readonly receipt: DemoPublicBookingReceipt | PublicBookingResponse;
  readonly embedded: boolean;
  readonly restart: () => void;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  const appointmentType = configuration.appointmentTypes.find(
    (type) => type.id === receipt.appointmentTypeId,
  );
  // The questionnaire and management pages refuse to be framed, so inside a clinic website
  // their links open a new tab instead.
  const linkTarget = embedded ? ({ target: "_blank", rel: "noopener noreferrer" } as const) : {};
  const newTabNote = embedded ? <span className="sr-only"> (opens in a new tab)</span> : null;
  return (
    <div className={`booking-app ${embedded ? "booking-embedded" : ""}`}>
      <BookingBrand clinicName={configuration.clinicName} />
      <main className="booking-confirmation" aria-labelledby="booking-confirmation-heading">
        <span className="success-mark">
          <Icon name="check" />
        </span>
        <p className="booking-eyebrow">Appointment booked</p>
        <h1 id="booking-confirmation-heading" ref={headingRef} tabIndex={-1}>
          {demoSchedulingEnabled()
            ? "Your synthetic appointment is on the schedule."
            : "Your appointment is on the schedule."}
        </h1>
        <p>The staff calendar can now see this booking.</p>
        <section className="booking-confirmation-ticket">
          <div>
            <span>When</span>
            <strong>
              {formatDateTimeRange(receipt.startAt, receipt.endAt, configuration.timeZone)}
            </strong>
          </div>
          <div>
            <span>Visit</span>
            <strong>
              {appointmentType?.name ?? "Appointment"} ·{" "}
              {slotDurationMinutes(receipt.startAt, receipt.endAt)} min
            </strong>
          </div>
          <div>
            <span>Clinician</span>
            <strong>{clinicianName(configuration, receipt.providerId)}</strong>
          </div>
          <small>
            <Icon name="location" />
            {configuration.clinicAddress}
          </small>
        </section>
        {receipt.intakeQuestionnaireUrl ? (
          <section className="booking-intake-callout" aria-labelledby="booking-intake-heading">
            <span>
              <Icon name="document" />
            </span>
            <div>
              <p className="booking-eyebrow">Required before arrival check-in</p>
              <h2 id="booking-intake-heading">Complete your new-patient questionnaire</h2>
              <p>
                Completing it before you arrive prepares the appointment but does not check you in
                automatically.
              </p>
              <a
                className="continue-button booking-link-button"
                href={receipt.intakeQuestionnaireUrl}
                {...linkTarget}
              >
                Complete new-patient questionnaire
                {newTabNote}
              </a>
            </div>
          </section>
        ) : null}
        {"reminders" in receipt ? (
          <section className="booking-simulation-receipt" aria-label="Reminder simulation">
            <Icon name="phone" />
            <div>
              <strong>
                {receipt.reminders.length
                  ? `${receipt.reminders.length} simulated confirmation receipt${receipt.reminders.length === 1 ? "" : "s"} recorded`
                  : "No confirmation receipt was requested"}
              </strong>
              <p>
                {receipt.reminders.length
                  ? receipt.reminders
                      .map(
                        (reminder) =>
                          `${reminder.channel.toUpperCase()} ${reminder.recipientMasked}`,
                      )
                      .join(" · ")
                  : "No text or email was sent."}
              </p>
              <small>No real message was sent by this proof of concept.</small>
            </div>
          </section>
        ) : null}
        {"manageAppointmentUrl" in receipt && receipt.manageAppointmentUrl ? (
          <div className="booking-manage-link">
            <a
              className="outline-button booking-link-button"
              href={receipt.manageAppointmentUrl}
              {...linkTarget}
            >
              Manage this appointment
              {newTabNote}
            </a>
            <p>Keep this private link. Use it to confirm, cancel or change your appointment.</p>
          </div>
        ) : null}
        <AddToCalendar
          appointment={{
            bookingReference: receipt.bookingReference,
            // A new booking's first file; a manage link's later files carry its version.
            sequence: 0,
            startAt: receipt.startAt,
            endAt: receipt.endAt,
            clinicName: configuration.clinicName,
            clinicAddress: configuration.clinicAddress,
          }}
        />
        <p className="booking-server-notice">{receipt.notice}</p>
        <p className="booking-reference">
          {demoSchedulingEnabled() ? "Demo reference" : "Booking reference"}{" "}
          <strong>{receipt.bookingReference}</strong>
        </p>
        <button type="button" className="outline-button" onClick={restart}>
          Book another appointment
        </button>
      </main>
    </div>
  );
}

export function BookingApp({
  embedded = false,
  clinicSlug,
  idleTimeoutMs = 10 * 60_000,
}: {
  readonly embedded?: boolean;
  readonly clinicSlug?: string | undefined;
  readonly idleTimeoutMs?: number;
}) {
  const demo = demoSchedulingEnabled();
  const queryClient = useQueryClient();
  const client = useMemo<PatientSchedulingClient>(
    () => (demo ? demoSchedulingClient : createPublicSchedulingClient(clinicSlug)),
    [demo, clinicSlug],
  );
  const [challengeToken, setChallengeToken] = useState("");
  const [challengeAttempt, setChallengeAttempt] = useState(0);
  const [step, setStep] = useState<BookingStep>(0);
  const [draft, setDraft] = useState<BookingDraft>(initialDraft);
  const [errors, setErrors] = useState<string[]>([]);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [receipt, setReceipt] = useState<DemoPublicBookingReceipt | PublicBookingResponse | null>(
    null,
  );
  // An existing patient's pass lives here only: never in a URL, storage or a query key.
  const [verifiedPatient, setVerifiedPatient] = useState<VerifiedPatient | null>(null);
  const [verificationExpired, setVerificationExpired] = useState(false);
  const [verificationLocked, setVerificationLocked] = useState(false);
  const idempotencyRef = useRef(idempotencyKey());
  const sessionGeneration = useRef(0);
  const bookingOperation = useRef<AbortController | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  const configurationQuery = useQuery({
    queryKey: ["booking-configuration", demo ? "demo" : clinicSlug],
    queryFn: ({ signal }) => client.configuration(signal),
  });
  const configuration = configurationQuery.data;
  // Framed by a website: the form shows only where the clinic listed every page around it, and
  // the height goes only to that parent's exact origin (embedParent.ts). The synthetic demo has
  // no list; an API older than PA-12 sends none, which lists no website.
  const embedContext = useMemo(() => readEmbedContext(), []);
  const parent =
    configuration === undefined
      ? undefined
      : embedParent(embedContext, demo ? "any" : (configuration.websiteOrigins ?? []));
  const parentOrigin = parent?.framed === true ? parent.origin : null;
  const existingPatientsOnly = configuration?.onlineBookingAudience === "existing_patients";
  // Only a clinic that books existing patients only asks for, and is sent, the pass.
  const verifiedForClinic = existingPatientsOnly ? verifiedPatient : null;
  const verification = verifiedForClinic?.verification;
  const healthCard = healthCardOffered(demo, configuration, draft);
  // The calendar runs on the clinic's clock: today there, through the booking window's last day.
  const today = localDateOf(new Date(), configuration?.timeZone);
  const lastDay = addDays(today, configuration?.bookingWindowDays ?? 0);
  const calendarMonth =
    draft.month !== "" ? draft.month : draft.date !== "" ? monthOf(draft.date) : monthOf(today);
  const availability = useAvailabilityCalendar({
    queryKey: [
      "booking-availability",
      demo ? "demo" : clinicSlug,
      draft.appointmentTypeId,
      draft.providerId,
      // Each pass gets its own results; its expiry time stands in for the secret itself.
      verifiedForClinic?.expiresAt ?? null,
    ],
    load: (range, signal) =>
      client.availability(
        {
          ...range,
          appointmentTypeId: draft.appointmentTypeId,
          ...(draft.providerId ? { providerId: draft.providerId } : {}),
        },
        signal,
        verification,
      ),
    month: calendarMonth,
    selected: draft.date,
    anchor: today,
    firstDay: today,
    lastDay,
    // Named times are one store row each; a clinic-assigned answer's may stand for more.
    exact: namesEveryTime,
    enabled:
      step === 1 &&
      draft.appointmentTypeId.length > 0 &&
      configuration !== undefined &&
      (!existingPatientsOnly || verification !== undefined),
  });
  const chosenDayTimes = draft.date === "" ? 0 : (availability.days.get(draft.date)?.length ?? 0);
  const bookingMutation = useMutation({
    gcTime: 0,
    mutationFn: ({
      input,
      consent,
      healthNumber,
      signal,
    }: {
      readonly input: CreateDemoPublicBookingRequest;
      readonly consent: PublicBookingConsent | undefined;
      readonly healthNumber: string | undefined;
      readonly signal: AbortSignal;
    }) =>
      client.createAppointment(input, signal, {
        turnstileToken: challengeToken,
        ...(verification === undefined ? {} : { verification }),
        ...(consent === undefined ? {} : { consent }),
        ...(healthNumber === undefined ? {} : { healthNumber }),
      }),
  });
  const resetBookingMutation = bookingMutation.reset;
  const clearBookingSession = useCallback(() => {
    sessionGeneration.current += 1;
    bookingOperation.current?.abort();
    bookingOperation.current = null;
    setDraft(initialDraft());
    setStep(0);
    setErrors([]);
    setReceipt(null);
    setVerifiedPatient(null);
    setVerificationExpired(false);
    setVerificationLocked(false);
    setChallengeToken("");
    setChallengeAttempt((current) => current + 1);
    resetBookingMutation();
    // The booking request holds the patient's details until it settles, even after reset. The
    // clinic configuration holds no patient data and stays, so closing does not refetch it.
    queryClient.getMutationCache().clear();
    queryClient.removeQueries({ queryKey: ["booking-availability"] });
    idempotencyRef.current = idempotencyKey();
    setSessionEnded(true);
  }, [queryClient, resetBookingMutation]);
  useEffect(() => {
    if (demo || sessionEnded) return;
    let timeout: ReturnType<typeof setTimeout>;
    const activity = () => {
      clearTimeout(timeout);
      timeout = setTimeout(clearBookingSession, idleTimeoutMs);
    };
    const restore = (event: PageTransitionEvent) => {
      if (event.persisted) clearBookingSession();
    };
    activity();
    window.addEventListener("pointerdown", activity);
    window.addEventListener("keydown", activity);
    window.addEventListener("pagehide", clearBookingSession);
    window.addEventListener("pageshow", restore);
    return () => {
      clearTimeout(timeout);
      bookingOperation.current?.abort();
      window.removeEventListener("pointerdown", activity);
      window.removeEventListener("keydown", activity);
      window.removeEventListener("pagehide", clearBookingSession);
      window.removeEventListener("pageshow", restore);
    };
  }, [clearBookingSession, demo, idleTimeoutMs, sessionEnded]);

  // The pass ran out (or was used) before the booking: confirm the details again from the start.
  const expireVerification = useCallback(() => {
    setVerifiedPatient(null);
    setVerificationExpired(true);
    setDraft(initialDraft());
    setStep(0);
    setErrors([]);
    queryClient.removeQueries({ queryKey: ["booking-availability"] });
  }, [queryClient]);
  // A 404 with no pass means the clinic changed who may book (or turned booking off) since the
  // page opened: read its settings again, which shows the verify step or the call page.
  const refetchConfiguration = configurationQuery.refetch;
  const availabilityError = availability.error;
  useEffect(() => {
    if (!(availabilityError instanceof DemoBookingApiError) || availabilityError.status !== 404) {
      return;
    }
    if (verification !== undefined) {
      expireVerification();
    } else {
      void refetchConfiguration();
    }
  }, [availabilityError, expireVerification, refetchConfiguration, verification]);

  useEffect(() => {
    document.title =
      configuration === undefined
        ? "Book an appointment · Bob Doc"
        : `Book an appointment · Bob Doc for ${configuration.clinicName}`;
  }, [configuration]);

  useEffect(() => {
    if (configuration !== undefined && demo) {
      canonicalizeBookingLocation(configuration.publicBookingPath);
    }
  }, [configuration, demo]);

  // Confirming an existing patient's details has its own heading (VerifyPatientStep).
  const verifying = existingPatientsOnly && verifiedPatient === null;
  useEffect(() => {
    if (configuration !== undefined && !verifying && stepLabels[step] !== undefined) {
      headingRef.current?.focus();
    }
  }, [step, configuration, verifying]);

  useEffect(() => {
    if (errors.length > 0) errorRef.current?.focus();
  }, [errors]);

  useEffect(() => {
    // Only a website the clinic listed is told the height, at its exact origin: a page opened
    // on its own with ?embed=1, or framed anywhere else, posts nothing.
    if (!embedded || parentOrigin === null || !("ResizeObserver" in window)) return;
    // The website sizes its frame from this height, so the document must be as tall as its
    // content and no taller: the class drops the full-viewport minimums (styles.css), or the
    // frame could grow between steps but never shrink back. scrollHeight never reports less
    // than the frame's own height, so measure the root element's box instead.
    const root = document.documentElement;
    root.classList.add("booking-embedded-document");
    const sendHeight = () => {
      window.parent.postMessage(
        { type: BOOKING_RESIZE_MESSAGE, height: Math.ceil(root.getBoundingClientRect().height) },
        parentOrigin,
      );
    };
    const observer = new ResizeObserver(sendHeight);
    observer.observe(root);
    sendHeight();
    return () => {
      observer.disconnect();
      root.classList.remove("booking-embedded-document");
    };
  }, [embedded, parentOrigin]);

  // Stable, so the calendar's choice of a first day does not run on every render.
  const update = useCallback((next: Partial<BookingDraft>) => {
    setDraft((current) => ({ ...current, ...next }));
    setErrors((current) => (current.length > 0 ? [] : current));
  }, []);

  const goToStep = (target: BookingStep) => {
    setStep(target);
    setErrors([]);
  };
  const back = () => {
    if (step > 0) goToStep((step - 1) as BookingStep);
  };

  const submitBooking = async () => {
    if (bookingOperation.current || sessionEnded) return;
    if (configuration === undefined || draft.slot === null || draft.sex === "") {
      setErrors(["The booking details are incomplete"]);
      return;
    }
    if (!demo && !challengeToken) {
      setErrors(["Complete the verification before booking."]);
      return;
    }
    // The clinic lane books only with the terms ticked for the version on screen.
    const documents = configuration.consentDocuments;
    const consent = documents === undefined ? undefined : bookingConsent(documents, draft.consent);
    if (consent === null) {
      setErrors([termsRequiredToBook]);
      return;
    }
    const operation = new AbortController();
    bookingOperation.current = operation;
    const generation = sessionGeneration.current;
    try {
      const result = await bookingMutation.mutateAsync({
        signal: operation.signal,
        consent,
        healthNumber: bookingHealthNumber(healthCard, draft),
        input: {
          patient: {
            kind: draft.kind,
            displayName: draft.displayName.trim(),
            dateOfBirth: draft.dateOfBirth,
            sex: draft.sex,
            phone: draft.phone.trim(),
            email: draft.email.trim(),
          },
          // A clinic-assigned time names no physician: the clinic picks one when it books.
          ...(draft.slot.providerId === undefined ? {} : { providerId: draft.slot.providerId }),
          appointmentTypeId: draft.appointmentTypeId,
          startAt: draft.slot.startAt,
          reason: draft.reason.trim(),
          idempotencyKey: idempotencyRef.current,
        },
      });
      if (generation !== sessionGeneration.current || operation.signal.aborted) return;
      setReceipt(result);
      setDraft(initialDraft());
      // A pass books once.
      setVerifiedPatient(null);
      resetBookingMutation();
      setChallengeToken("");
      setErrors([]);
    } catch (error) {
      if (generation !== sessionGeneration.current || operation.signal.aborted) return;
      setChallengeToken("");
      setChallengeAttempt((current) => current + 1);
      // New terms were published after the page loaded, and nothing was saved: show the new
      // text and ask again, keeping the chosen time. Not a booking conflict.
      if (error instanceof DemoBookingApiError && error.code === PUBLIC_TERMS_CHANGED_ERROR_CODE) {
        setDraft((current) => ({
          ...current,
          consent: { ...current.consent, termsAcceptedVersion: null },
        }));
        void refetchConfiguration();
        setErrors([termsChanged]);
        return;
      }
      if (error instanceof DemoBookingApiError && error.status === 404) {
        if (verification !== undefined) {
          expireVerification();
          return;
        }
        void refetchConfiguration();
      }
      const conflict = error instanceof DemoBookingApiError && error.status === 409;
      if (conflict) {
        // Back to the same day's times, read again without the one just taken: marked stale
        // here, they are asked for once when the date and time step turns them back on.
        setDraft((current) => ({ ...current, slot: null }));
        setStep(1);
        void queryClient.invalidateQueries({
          queryKey: ["booking-availability"],
          refetchType: "none",
        });
      }
      setErrors([
        conflict
          ? "That time was just booked. Choose another time."
          : // A request that fails its schema check would otherwise show the raw issue list.
            isSchemaError(error)
            ? "Some booking details could not be accepted. Check them and try again."
            : error instanceof Error
              ? error.message
              : "The appointment could not be booked",
      ]);
    } finally {
      if (bookingOperation.current === operation) bookingOperation.current = null;
    }
  };

  const next = () => {
    if (configuration === undefined) return;
    const nextErrors = validateStep(step, draft, configuration, healthCard);
    if (nextErrors.length > 0) {
      setErrors(nextErrors);
      return;
    }
    if (step < 3) {
      setStep((step + 1) as BookingStep);
      setErrors([]);
      window.scrollTo({ top: 0, behavior: scrollBehavior() });
      return;
    }
    void submitBooking();
  };

  const restart = () => {
    clearBookingSession();
    setSessionEnded(false);
  };

  if (sessionEnded) {
    return (
      <div className="booking-app">
        <main className="booking-state-card">
          <h1>Booking session closed</h1>
          <p>
            Your details have been cleared from this page. If you submitted an appointment, contact
            the clinic to confirm before booking again.
          </p>
          <button type="button" className="continue-button" onClick={restart}>
            Start again
          </button>
        </main>
      </div>
    );
  }

  if (configurationQuery.isPending) {
    return (
      <div className={`booking-app ${embedded ? "booking-embedded" : ""}`}>
        <BookingBrand clinicName="the clinic" />
        <main className="booking-state-card booking-loading" role="status">
          <span className="button-spinner" />
          <h1>Opening the appointment calendar…</h1>
        </main>
      </div>
    );
  }

  if (configurationQuery.isError || configuration === undefined) {
    // The service answers 404 for an unknown, renamed or deactivated clinic slug: the link is
    // wrong, and retrying it can never succeed.
    const invalidLink =
      !demo &&
      configurationQuery.error instanceof DemoBookingApiError &&
      configurationQuery.error.status === 404;
    return (
      <ConfigurationError
        embedded={embedded}
        invalidLink={invalidLink}
        retry={() => void configurationQuery.refetch()}
      />
    );
  }

  if (parent?.framed === true && parent.origin === null) {
    return <EmbedNotAllowed bookingPath={configuration.publicBookingPath} embedded={embedded} />;
  }

  if (!isOnlineBookingOpen(configuration)) {
    return <BookingUnavailable configuration={configuration} embedded={embedded} />;
  }

  if (receipt !== null) {
    return (
      <BookingConfirmation
        configuration={configuration}
        receipt={receipt}
        embedded={embedded}
        restart={restart}
      />
    );
  }

  const verify = client.verify;
  if (existingPatientsOnly && verificationLocked) {
    return <VerificationLocked configuration={configuration} embedded={embedded} />;
  }
  if (existingPatientsOnly && verify === undefined) {
    return <BookingUnavailable configuration={configuration} embedded={embedded} />;
  }

  return (
    <div className={`booking-app ${embedded ? "booking-embedded" : ""}`}>
      <BookingBrand clinicName={configuration.clinicName} />
      <div className="booking-layout">
        <aside className="booking-sidebar">
          <p className="booking-eyebrow">Online appointments</p>
          <h1>{configuration.clinicName}</h1>
          <p>{configuration.clinicAddress}</p>
          <ClinicHoursToday hours={configuration.clinicHours} timeZone={configuration.timeZone} />
          {demo ? (
            <div className="booking-sidebar-note">
              <Icon name="shield" />
              <p>
                <strong>Synthetic demo only</strong>
                Use made-up details.
              </p>
            </div>
          ) : null}
        </aside>
        <main className="booking-main">
          <div className="booking-mobile-heading">
            <p className="booking-eyebrow">{configuration.clinicName}</p>
            <ClinicHoursToday hours={configuration.clinicHours} timeZone={configuration.timeZone} />
          </div>
          {verifying ? null : (
            <StepProgress
              label="Booking progress"
              steps={stepLabels}
              current={step}
              // A finished step goes back to it, except while the booking is being sent.
              {...(bookingMutation.isPending
                ? {}
                : { onSelect: (index: number) => goToStep(index as BookingStep) })}
            />
          )}
          {verifying && verify !== undefined ? (
            <VerifyPatientStep
              verify={verify}
              turnstileSiteKey={demo ? undefined : configuration.turnstileSiteKey}
              today={dateAfter(0)}
              expired={verificationExpired}
              onVerified={(patient) => {
                setVerifiedPatient(patient);
                setVerificationExpired(false);
                setDraft({
                  ...initialDraft(),
                  kind: "returning",
                  dateOfBirth: patient.dateOfBirth,
                });
                setStep(0);
                setErrors([]);
              }}
              onLocked={() => setVerificationLocked(true)}
              onUnavailable={() => void configurationQuery.refetch()}
            />
          ) : null}
          {!verifying && errors.length > 0 ? (
            <div className="error-summary" role="alert" tabIndex={-1} ref={errorRef}>
              <strong>Please check the following:</strong>
              <ul>
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {verifying ? null : (
            <form
              className="booking-card"
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                next();
              }}
            >
              {step > 0 ? (
                <button
                  type="button"
                  className="booking-back-link"
                  onClick={back}
                  disabled={bookingMutation.isPending}
                >
                  <Icon name="chevronLeft" />
                  {backLabels[step]}
                </button>
              ) : null}
              <header className="booking-step-header">
                <h1 ref={headingRef} tabIndex={-1}>
                  {stepHeadings[step]}
                </h1>
                {/* Honest about what a shown time means: nothing is reserved yet. */}
                {step === 1 ? <span>Times aren't held until you book.</span> : null}
                {/* And about what these details do: the clinic reviews them; no chart opens. */}
                {step === 2 && !demo && verifiedForClinic === null ? (
                  <span>Booking doesn't create or open a patient chart.</span>
                ) : null}
              </header>

              {step === 0 ? (
                <VisitStep
                  configuration={configuration}
                  draft={draft}
                  update={update}
                  verifiedPatient={verifiedForClinic}
                />
              ) : null}
              {step === 1 ? (
                <TimeStep
                  configuration={configuration}
                  draft={draft}
                  update={update}
                  availability={availability}
                  today={today}
                  lastDay={lastDay}
                  month={calendarMonth}
                />
              ) : null}
              {step === 2 ? (
                <DetailsStep
                  draft={draft}
                  update={update}
                  verified={verifiedForClinic !== null}
                  healthCard={healthCard}
                />
              ) : null}
              {step === 3 ? (
                <ReviewStep
                  configuration={configuration}
                  draft={draft}
                  update={update}
                  embedded={embedded}
                  healthCard={healthCard}
                  edit={goToStep}
                  busy={bookingMutation.isPending}
                />
              ) : null}
              {step === 3 && !demo && configuration.turnstileSiteKey ? (
                <BookingChallenge
                  key={challengeAttempt}
                  siteKey={configuration.turnstileSiteKey}
                  onToken={setChallengeToken}
                />
              ) : null}

              <footer className="booking-actions">
                <button
                  type="submit"
                  className="continue-button"
                  disabled={
                    bookingMutation.isPending ||
                    (step === 3 && !demo && !challengeToken) ||
                    // The terms on screen may be about to change.
                    (step === 3 && configurationQuery.isFetching) ||
                    // Nothing to pick yet: the times are loading, or the chosen day has none.
                    (step === 1 &&
                      draft.slot === null &&
                      (availability.pending || chosenDayTimes === 0))
                  }
                >
                  {bookingMutation.isPending ? (
                    <>
                      <span className="button-spinner" />
                      Booking…
                    </>
                  ) : step === 3 ? (
                    <>
                      <Icon name="check" />
                      Book appointment
                    </>
                  ) : (
                    <>
                      Continue
                      <Icon name="arrow" />
                    </>
                  )}
                </button>
              </footer>
            </form>
          )}
          <p className="booking-footer-note">
            <Icon name="lock" />
            {demo ? "Demo · nothing is sent" : "Private booking · Nothing is saved to this device"}
          </p>
        </main>
      </div>
    </div>
  );
}
